export function formatCompactNumber(value) {
  const numericValue = Number(value || 0);
  if (numericValue >= 1000000) {
    return `${(numericValue / 1000000).toFixed(1)}M`;
  }
  if (numericValue >= 1000) {
    return `${Math.round(numericValue / 1000)}K`;
  }
  return `${numericValue}`;
}

export function formatDate(value) {
  if (!value) return "--";

  let timestamp = value;
  // Numeric timestamps may be provided in seconds instead of milliseconds.
  // Unix seconds for current dates are ~10 digits (< 1e12), while millisecond
  // timestamps are ~13 digits, so scale up anything below that threshold.
  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(numeric) && /^\s*-?\d+(\.\d+)?\s*$/.test(`${value}`)) {
    timestamp = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "--";

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export function normalizeTags(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => `${item}`.trim())
      .filter((item) => item !== "");
  }
  if (typeof value === "string") {
    return value.trim() ? [value.trim()] : [];
  }
  return [];
}

export function normalizePlaceRecord(record = {}, options = {}) {
  const placematrixId = String(record.placematrix_id || "");
  const placematrixTokens = placematrixId.split("_");
  const sourceToken = (placematrixTokens[1] || "").toUpperCase();
  // The placematrix token is the usual source signal, but it is unreliable for
  // Custom POIs (Point places): they carry no polygon placematrix_id, so the
  // token normalizes to "Azira" even though the record came from the Custom
  // library. When the caller knows it is browsing the Custom source
  // (options.sourceFilter === "Custom"), that is authoritative — every record
  // in a Custom-source response is a Custom place regardless of the token. This
  // mirrors how PlacesPage drives `custom` from the active source filter rather
  // than row.source.
  const source =
    options.sourceFilter === "Custom" || sourceToken === "C" ? "Custom" : "Azira";
  const category = record.cat1 || record.category || null;
  const subcategory = record.cat2 || record.subcategory || null;

  return {
    id: `${record.near_poi_id || ""}-${record.vp_id || ""}-${record.unique_name || ""}`,
    near_poi_id: record.near_poi_id || "",
    placematrix_id: record.placematrix_id || null,
    name: record.unique_name || record.name || "--",
    source,
    address: record.address || record.address_full || "--",
    // Per-place country (e.g. "ARE") from the upstream POI record. Retained so
    // the View/Edit/Clone modals can fetch full detail from the correct country
    // partition instead of the hardcoded USA default.
    country: record.country || "",
    category: subcategory || category || "--",
    brand: record.poi_list_name || record.brand || "--",
    tags: normalizeTags(record.labels || record.tags),
    // Prefer the explicit type from the record; otherwise infer it: a
    // placematrix_id implies a Polygon place, and its absence implies a POI.
    type: record.type || (placematrixId ? "Polygon" : "POI"),
    lastUpdated: record.updated_at || record.created_at || null,
  };
}

export function getFavoriteCategory(rows = []) {
  if (!rows.length) return "--";
  const counts = rows.reduce((map, row) => {
    const category = row.category || "Unknown";
    map[category] = (map[category] || 0) + 1;
    return map;
  }, {});

  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || "--";
}

export function countRowsUpdatedThisMonth(rows = []) {
  const now = new Date();
  return rows.filter((row) => {
    if (!row.lastUpdated) return false;
    const updated = new Date(row.lastUpdated);
    if (Number.isNaN(updated.getTime())) return false;

    return updated.getMonth() === now.getMonth() && updated.getFullYear() === now.getFullYear();
  }).length;
}
/**
 * Determine whether a value is a finite integer within an inclusive bound.
 */
function isIntegerInRange(value, min, max) {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
}

/**
 * Offset-mode range. Returns { start, end, total, valid }.
 * `valid === false` signals "count unavailable" (Req 2.5) so the caller can
 * suppress the range and retain the last valid label. A valid computation with
 * `total === 0` returns { start: 0, end: 0, total: 0, valid: true }.
 */
export function computeOffsetRange(page, pageSize, total /*, displayedCount */) {
  const pageValid = isIntegerInRange(page, 1, Number.MAX_SAFE_INTEGER);
  const pageSizeValid = isIntegerInRange(pageSize, 1, 100);
  const totalValid = isIntegerInRange(total, 0, 999999999);

  if (!pageValid || !pageSizeValid || !totalValid) {
    return { valid: false };
  }

  if (total === 0) {
    return { start: 0, end: 0, total: 0, valid: true };
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return { start, end, total, valid: true };
}

/**
 * Cursor-mode range. `pageSize` is fixed at 100 by the caller.
 * Empty window => { start: 0, end: 0, total, valid: true } (Req 3.4).
 * Invalid/absent total => total coerced to 0, still valid (Req 3.5).
 */
export function computeCursorRange(stackLength, pageSize, total, displayedCount) {
  if (!isIntegerInRange(stackLength, 0, Number.MAX_SAFE_INTEGER)) {
    return { valid: false };
  }

  const safeTotal = isIntegerInRange(total, 0, Number.MAX_SAFE_INTEGER) ? total : 0;

  if (!(displayedCount > 0)) {
    return { start: 0, end: 0, total: safeTotal, valid: true };
  }

  const start = stackLength * pageSize + 1;
  const end = Math.min(start + displayedCount - 1, safeTotal);
  return { start, end, total: safeTotal, valid: true };
}

/**
 * Exact thousands-separated integer string, no abbreviation (Req 5).
 * Returns null when value is not a finite non-negative whole number (Req 5.4).
 * Does NOT reuse formatCompactNumber — no rounding, scaling, or abbreviation.
 */
export function formatThousands(value) {
  if (
    value === null ||
    value === undefined ||
    typeof value !== "number" ||
    Number.isNaN(value) ||
    !Number.isFinite(value) ||
    value < 0 ||
    !Number.isInteger(value)
  ) {
    return null;
  }

  return new Intl.NumberFormat("en-US").format(value);
}
